import { useEffect, useRef } from 'react';

// Shared modal behaviour: close on Escape, move focus into the dialog when it
// opens, return focus to whatever opened it, and stop the page scrolling behind.
//
// Returns a ref to attach to the dialog panel.
export default function useDialog(onClose) {
  const dialogRef = useRef(null);

  // Callers pass inline arrow functions, so keep the latest one in a ref and
  // leave the effect's dependencies empty — otherwise it would tear down and
  // re-run on every render, stealing focus back each time.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previouslyFocused = document.activeElement;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    const panel = dialogRef.current;
    if (panel) {
      const focusable = panel.querySelector(
        'input:not([type="hidden"]), textarea, select, button, [href]'
      );
      (focusable || panel).focus();
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, []);

  return dialogRef;
}
